// The Admin page's Billing tab (RFC-065 model 7, byj-1wv.132.8): every
// plan open now, those whose last charge was refused first, and the latest
// payments, each with whose it is; a paid one refunded through the payment
// provider after asking, which ends the plan it opened. What the relay
// says, nothing estimated; never a billing key.
import { useCallback, useEffect, useState } from "react";
import { api, type AdminBilling as AdminBillingView } from "../api";
import { price } from "../billing";
import { t, whenDay } from "../i18n";
import { useConfirm } from "./Confirm";
import { RowMenu, type MenuItem } from "./RowMenu";
import { EmptyRows, Grouped, initialOf } from "./Rows";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export type AdminBillingApi = Pick<typeof api, "adminBilling" | "adminRefund">;

type SubRow = AdminBillingView["subscriptions"][number];
type OrderRow = AdminBillingView["orders"][number];
/** A row of the tab: an open plan, or a payment. */
type Row = { kind: "sub"; x: SubRow } | { kind: "order"; o: OrderRow };

function PlanRow({ x }: { x: SubRow }) {
  const when = x.renews ? t("billing:renewsOn", { w: whenDay(x.period_end), card: x.card ?? "" }) : t("billing:endsOn", { w: whenDay(x.period_end) });
  return (
    <li className={`share-row admin-row no-menu ${x.status === "active" ? "is-live" : "is-ended"}`} data-testid="billing-subscription">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(x.email)}
      </span>
      <div className="row-main">
        <span className="row-title">{x.email}</span>
        <div className="row-sub">{[x.plan.name, when].join(" · ")}</div>
      </div>
      <div className="row-meta">{x.status === "past_due" && <span className="chip chip-warn">{t("admin:adminBillingPastDue")}</span>}</div>
    </li>
  );
}

function PaymentRow({ o, onRefund }: { o: OrderRow; onRefund: (o: OrderRow) => void }) {
  const menu: MenuItem[] = o.status === "paid" ? [{ label: t("admin:adminBillingRefund"), onSelect: () => onRefund(o), danger: true }] : [];
  return (
    <li className={`share-row admin-row ${o.status === "paid" ? "is-live" : "is-ended"}`} data-testid="billing-order">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(o.email)}
      </span>
      <div className="row-main">
        <span className="row-title">{o.email}</span>
        <div className="row-sub">{[o.name, price(o.amount, o.currency), o.method, whenDay(o.paid_at ?? o.created_at)].filter(Boolean).join(" · ")}</div>
      </div>
      <div className="row-meta">
        <span className={`chip${o.status === "failed" ? " chip-warn" : ""}`}>{t(`billing:order_${o.status}`)}</span>
      </div>
      <RowMenu label={t("shares:moreFor", { title: o.email })} items={menu} />
    </li>
  );
}

export function AdminBilling({ client, onCount }: { client: AdminBillingApi; onCount?: (n: number) => void }) {
  const [data, setData] = useState<AdminBillingView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [ask, confirmDialog] = useConfirm();
  const load = useCallback(async () => {
    try {
      const d = await client.adminBilling();
      setData(d);
      onCount?.(d.subscriptions.length);
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  }, [client, onCount]);
  useEffect(() => void load(), [load]);

  const refund = async (o: OrderRow) => {
    if (!(await ask(t("admin:adminBillingRefundAsk", { email: o.email, amount: price(o.amount, o.currency) }), { action: t("admin:adminBillingRefund"), danger: true }))) return;
    try {
      await client.adminRefund(o.id);
      setDone(t("admin:adminBillingRefunded", { email: o.email }));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
    await load();
  };

  return (
    <div className="stack" data-testid="admin-billing">
      <p className="muted small panel-lead">{data && !data.configured ? t("billing:off") : t("admin:adminBillingLead")}</p>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {done && (
        <p className="ok-text" role="status">
          {done}
        </p>
      )}
      {!data && !error && <p className="muted">{t("common:loading")}</p>}
      {data && (
        <Grouped<Row>
          id="admin-billing"
          headed
          groups={[
            { title: t("admin:adminBillingPlans"), items: data.subscriptions.map((x) => ({ kind: "sub" as const, x })), none: <EmptyRows text={t("admin:adminBillingNoPlans")} /> },
            { title: t("admin:adminBillingPayments"), items: data.orders.map((o) => ({ kind: "order" as const, o })), none: <EmptyRows text={t("billing:noOrders")} /> },
          ]}
          row={(r) => (r.kind === "sub" ? <PlanRow key={r.x.id} x={r.x} /> : <PaymentRow key={r.o.id} o={r.o} onRefund={(o) => void refund(o)} />)}
        />
      )}
      {confirmDialog}
    </div>
  );
}
